import { motion } from "framer-motion";
import Layout from "../components/Layout";
import { container, item } from "../lib/motion";
import { LINKS } from "../lib/links";
import { BRAND } from "../lib/brand";
import { SERVICES, WHY, INQUIRY_BULLETS } from "../data/business";
import { ArrowRight, Mail, ImageOff, CalendarClock, Check } from "lucide-react";

function Section({ children, className = "", id }) {
  return (
    <motion.section id={id} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-90px" }}
      variants={container} className={`max-w-6xl mx-auto px-6 md:px-10 ${className}`}>
      {children}
    </motion.section>
  );
}

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function Business() {
  return (
    <Layout glow="top" backdrop={BRAND.atmosphere}>
      <div data-testid="business-page">
        {/* HERO */}
        <section className="relative overflow-hidden py-28 md:py-36" data-testid="business-hero">
          <div className="absolute inset-0">
            <motion.div className="absolute top-0 left-1/2 -translate-x-1/2 w-[640px] h-[640px] bg-[#4da6ff] rounded-full blur-[180px]"
              animate={{ opacity: [0.07, 0.14, 0.07], scale: [1, 1.07, 1] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
          </div>
          <motion.div initial="hidden" animate="show" variants={container} className="relative max-w-3xl mx-auto px-6 md:px-10 text-center">
            <motion.span variants={item} className="eyebrow">Spirit Admin for Business</motion.span>
            <motion.h1 variants={item} className="font-head text-4xl sm:text-5xl lg:text-6xl font-light mt-5 tracking-tight leading-[1.02]">
              Short-Form Content<br /><span className="font-semibold">That Keeps You Seen.</span>
            </motion.h1>
            <motion.p variants={item} className="text-zinc-400 text-lg md:text-xl mt-7 leading-relaxed font-light">
              We help businesses stay visible through consistent, high-quality short-form content for Facebook, TikTok, and other short-form platforms — planned, edited, and delivered ready to post.
            </motion.p>
            <motion.div variants={item} className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <button onClick={() => scrollTo("business-contact")} data-testid="business-work-with-us" className="btn-primary font-medium px-8 py-4 flex items-center justify-center gap-2.5">
                Work With Us <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={() => scrollTo("recent-work")} data-testid="business-view-portfolio" className="btn-glass font-medium px-8 py-4 flex items-center justify-center gap-2.5">
                View Portfolio
              </button>
            </motion.div>
          </motion.div>
        </section>

        {/* SERVICES */}
        <Section className="py-20" id="services">
          <motion.div variants={item} className="text-center mb-14">
            <span className="eyebrow">What We Do</span>
            <h2 className="font-head text-4xl lg:text-5xl font-light mt-4 tracking-tight">Services</h2>
            <p className="text-zinc-500 mt-5 max-w-2xl mx-auto font-light text-lg">A professional content service that keeps your brand showing up — consistently and on time.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => (
              <motion.div key={s.label} variants={item} className="glass-card p-7 group" data-testid={`service-${s.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                <div className="w-11 h-11 rounded-2xl glass flex items-center justify-center mb-5 group-hover:glow-blue transition-all"><s.icon className="w-5 h-5 text-[#4da6ff]" strokeWidth={1.5} /></div>
                <h3 className="font-head text-lg font-medium">{s.label}</h3>
                <p className="text-zinc-500 text-sm mt-2 font-light leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* WHY WORK WITH SPIRIT ADMIN */}
        <Section className="py-24">
          <motion.div variants={item} className="text-center mb-14">
            <span className="eyebrow">The Difference</span>
            <h2 className="font-head text-4xl lg:text-5xl font-light mt-4 tracking-tight">Why work with Spirit Admin</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {WHY.map((w) => (
              <motion.div key={w.label} variants={item} className="glass-card p-8 text-center" data-testid={`why-${w.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-12 h-12 rounded-2xl glass flex items-center justify-center mx-auto mb-5"><w.icon className="w-5 h-5 text-[#4da6ff]" strokeWidth={1.5} /></div>
                <h3 className="font-head text-lg font-medium">{w.label}</h3>
                <p className="text-zinc-500 text-sm mt-2.5 font-light leading-relaxed">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* RECENT WORK — portfolio placeholder */}
        <Section className="py-20" id="recent-work">
          <motion.div variants={item} className="text-center mb-12">
            <span className="eyebrow">Recent Work</span>
            <h2 className="font-head text-4xl lg:text-5xl font-light mt-4 tracking-tight">Selected projects</h2>
          </motion.div>
          <motion.div variants={item} className="relative glass-card p-16 md:p-20 text-center overflow-hidden" data-testid="portfolio-placeholder">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] bg-[#4da6ff]/8 blur-[110px] rounded-full" />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl glass flex items-center justify-center mx-auto mb-6"><ImageOff className="w-6 h-6 text-[#4da6ff]" strokeWidth={1.5} /></div>
              <h3 className="font-head text-2xl md:text-3xl font-light tracking-tight">Portfolio Coming Soon</h3>
              <p className="text-zinc-500 mt-4 max-w-md mx-auto font-light">We're curating a selection of our best client work. Check back shortly.</p>
            </div>
          </motion.div>
        </Section>

        {/* CONTACT */}
        <Section className="py-24" id="business-contact">
          <motion.div variants={item} className="relative glass-card p-12 md:p-16 text-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#4da6ff]/12 via-transparent to-transparent" />
            <div className="relative z-10 max-w-xl mx-auto">
              <span className="eyebrow">Contact</span>
              <h2 className="font-head text-4xl lg:text-[3rem] font-light mt-4 tracking-tight leading-[1.08]">Let's build something<br /><span className="font-semibold">worth watching.</span></h2>
              <p className="text-zinc-400 mt-5 font-light text-lg">Tell us what you're working on and we'll shape an AI-driven plan around it.</p>
              <a href={`mailto:${LINKS.contactEmail}`} data-testid="business-contact-email" className="inline-flex items-center gap-2 mt-8 text-[#4da6ff] hover:text-white transition-colors text-lg">
                <Mail className="w-5 h-5" /> {LINKS.contactEmail}
              </a>
            </div>
          </motion.div>
        </Section>

        {/* BUSINESS INQUIRIES — booking placeholder (Calendly-ready) */}
        <Section className="py-20 pb-32">
          <motion.div variants={item} className="relative glass-card p-12 md:p-16 text-center overflow-hidden" data-testid="business-inquiries">
            <div className="absolute -top-12 -right-10 w-52 h-52 bg-[#4da6ff]/15 blur-[80px] rounded-full" />
            <div className="relative z-10 max-w-xl mx-auto">
              <div className="w-12 h-12 rounded-2xl glass flex items-center justify-center mx-auto mb-6"><CalendarClock className="w-6 h-6 text-[#4da6ff]" strokeWidth={1.5} /></div>
              <h2 className="font-head text-3xl lg:text-4xl font-light tracking-tight">Business Inquiries</h2>
              <p className="text-zinc-400 mt-5 font-light text-lg leading-relaxed">
                Every business is different. Email us and we'll help build a simple content plan that fits your goals, budget, and schedule.
              </p>
              <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-left max-w-md mx-auto">
                {INQUIRY_BULLETS.map((b) => (
                  <li key={b} className="flex items-center gap-2.5 text-sm text-zinc-300 font-light">
                    <Check className="w-4 h-4 text-[#4da6ff] shrink-0" strokeWidth={2} /> {b}
                  </li>
                ))}
              </ul>
              {/* Calendly embed will drop in here later. */}
              <a href={`mailto:${LINKS.contactEmail}`} data-testid="business-email-us" className="btn-primary font-medium px-8 py-3.5 mt-9 inline-flex items-center gap-2.5">
                <Mail className="w-4 h-4" /> Email Us
              </a>
            </div>
          </motion.div>
        </Section>
      </div>
    </Layout>
  );
}
